import { ProjectReportStatus } from "@/domain/project-reports/types";
import type {
  ProjectReportContext,
  ProjectReportContextExpand,
  ProjectReportProps,
} from "@/domain/project-reports/types";

export class ProjectReport {
  private constructor(private readonly props: ProjectReportProps) {
    this.validate();
  }

  static create(props: ProjectReportProps): ProjectReport {
    const nextProps: ProjectReportProps = {
      ...props,
      status: props.status ?? "Saved",
      content: props.content ?? null,
      context: props.context ?? null,
      contextExpand: props.contextExpand ?? null,
      category: props.category ?? null,
      isPublic: props.isPublic ?? false,
      isArchived: props.isArchived ?? false,
      isDeleted: props.isDeleted ?? false,
    };

    return new ProjectReport(nextProps);
  }

  static restore(props: ProjectReportProps): ProjectReport {
    return new ProjectReport(props);
  }

  withStatus(status: ProjectReportStatus): ProjectReport {
    return new ProjectReport({
      ...this.props,
      status,
    });
  }


  markArchived(): ProjectReport {
    return new ProjectReport({
      ...this.props,
      isArchived: true,
    });
  }

  markDeleted(): ProjectReport {
    return new ProjectReport({
      ...this.props,
      isDeleted: true,
    });
  }

  updateContext(context: ProjectReportContext, contextExpand: ProjectReportContextExpand): ProjectReport {
    return new ProjectReport({
      ...this.props,
      context,
      contextExpand,
    });
  }

  toObject(): ProjectReportProps {
    return { ...this.props };
  }

  get id(): string | undefined {
    return this.props.id;
  }

  get projectId(): string {
    return this.props.projectId;
  }

  get promptsIds(): string[] {
    return this.props.promptsIds;
  }

  get recordsIds(): string[] {
    return this.props.recordsIds;
  }

  get status(): ProjectReportStatus {
    return this.props.status;
  }

  get title(): string {
    return this.props.title;
  }

  private validate() {
    if (!this.props.projectId || this.props.projectId.trim().length === 0) {
      throw new Error("ProjectReport requires projectId");
    }
    // Allow empty title - it will show placeholder (preset name) in UI
    if (this.props.title === undefined || this.props.title === null) {
      throw new Error("ProjectReport title must be a string (can be empty)");
    }
    if (!Array.isArray(this.props.promptsIds) || this.props.promptsIds.length === 0) {
      throw new Error("ProjectReport requires at least one prompt id");
    }
    if (!Array.isArray(this.props.recordsIds) || this.props.recordsIds.length === 0) {
      throw new Error("ProjectReport requires at least one record id");
    }
  }
}
